import { ReactNode } from 'react'
import CopyButton from '@/components/copy-button'
import { cx } from '@/utils/strings'
import GradientBackgroundWrapper from './gradient-background-wrapper'
import backgroundStyles from '@/constants/background-styles'
import { useInspectorProps } from '@/utils/live-preview'
import { useTranslations } from '@/utils/locale'

export type CalloutProps = {
  contentful_id: string
  type: 'info' | 'warning' | 'success' | 'example'
  className?: string
  children: ReactNode
  hideHeader?: boolean
}

export default function Callout({ contentful_id: id, type, children, className, hideHeader = false }: CalloutProps) {
  const pageInspectorProps = useInspectorProps({ entryId: id, restrictedTypes: ['pages'] })
  const { t } = useTranslations()

  // Literal t() calls so ctf:i18n-tags discovers these callout labels.
  const title = {
    warning: t('warning'),
    info: t('note'),
    success: t('tip'),
    example: t('example'),
  }
  const icons = {
    warning: '\uf193',
    info: '\uf164',
    success: '\uf19f',
    example: '\uf19f',
  }
  return (
    <GradientBackgroundWrapper
      backgroundStyle={backgroundStyles.gradient2}
      className="mx-0! mb-8 rounded-sm! after:rounded-sm!"
      innerClassName="p-0 rounded-sm!"
      {...pageInspectorProps({ fieldId: 'title' })}
    >
      <div className={cx('callout', className)}>
        {!hideHeader && (
          <div
            className={cx(
              'flex items-center gap-2 px-5 py-2',
              type === 'warning' && `bg-warning text-white`,
              type === 'info' && ` bg-primaryBlue text-white dark:text-black`,
              type === 'success' && `bg-accent-teal text-black`,
              type === 'example' && `bg-bw-deep-blue text-white`
            )}
          >
            <span className="font-bwi text-2xl leading-[30px] font-light translate-y-px">{icons[type]}</span>
            <span className={cx(`text-2xl leading-none capitalize py-0 font-medium`)}>{title[type]}</span>
            {type === 'example' && (
              <div className="flex-1 flex justify-end">
                <CopyButton id={id} theme="light" className="text-sm" />
              </div>
            )}
          </div>
        )}
        <section id={id} className="p-5 [&>p:last-child]:mb-1">
          {children}
        </section>
      </div>
    </GradientBackgroundWrapper>
  )
}
